<template>
  <header class="pc-header">
    <div class="header-content">
      <div class="logo" @click="handleLogoClick">
        <img src="@/assets/pc-logo.png" alt="Jingsun Car" class="logo-img" />
      </div>
      <nav class="nav">
        <router-link v-for="item in navList" :key="item.name" :to="item.path" :class="{ 'nav-item-active': $route.fullPath === item.path }" class="nav-item">
          {{ item.name }}
        </router-link>
      </nav>
      <div class="header-actions">
        <div class="language-dropdown" ref="languageDropdown" @click.stop="showLanguageMenu = !showLanguageMenu">
          <span class="en-text">{{ currentLanguageDisplay }}</span>
          <svg class="arrow-icon" :class="{ rotate: showLanguageMenu }" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <!-- 下拉菜单 -->
          <div v-if="showLanguageMenu" class="language-dropdown-menu" @click.stop>
            <div v-for="lang in languageList" :key="lang.value" class="language-option" :class="{ active: currentLanguage === lang.value }" @click.stop="handleLanguageSelect(lang.value)">
              {{ lang.label }}
            </div>
          </div>
        </div>
		
		<div  class="language-dropdown" @click="goFavorites">
		  <div style="width: 30px;height: 30px;">
		  <svg class="heart_img" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M12 6.00019C10.2006 3.90317 7.19377 3.2551 4.93923 5.17534C2.68468 7.09558 2.36727 10.3061 4.13778 12.5772C5.60984 14.4654 10.0648 18.4479 11.5249 19.7369C11.6882 19.8811 11.7699 19.9532 11.8652 19.9815C11.9483 20.0062 12.0393 20.0062 12.1225 19.9815C12.2178 19.9532 12.2994 19.8811 12.4628 19.7369C13.9229 18.4479 18.3778 14.4654 19.8499 12.5772C21.6204 10.3061 21.3417 7.07538 19.0484 5.17534C16.7551 3.2753 13.7994 3.90317 12 6.00019Z" stroke="#000000" stroke-width="0.24000000000000005" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
		  </div>
		  <div><span style="font-size: 14px;">{{ $t('details_page.view_my_favorites')}}</span></div>
		</div>
		
        <!-- 未登录状态：显示登录/注册按钮 -->
        <template v-if="!isLoggedIn">
          <div class="login-btn" @click="handleLogin">
            <img src="@/assets/login-icon.png" alt="login" />
            <span>{{ $t('login_page.login_button_title') }}</span>
          </div>
          <div class="login-btn register-btn" @click="handleRegister">{{ $t('register_page.register_button_title') }}</div>
        </template>
        <!-- 已登录状态：显示退出按钮 -->
        <div v-else class="login-btn logout-btn" @click="handleLogout">
          <span>{{ $t('login_page.logout_button_title') }}</span>
        </div>
      </div>
    </div>

    <!-- 登录/注册弹框 -->
    <LoginModal :visible="showLogin || showRegister" :mode="showLogin ? 'login' : 'register'" @close="handleCloseModal" />
  </header>
</template>

<script>
import { getToken, removeToken } from '@/utils/auth'
import { getLanguage, setLanguage } from '@/utils/lang'
import { reloadFulltext } from '@/utils/i18n'
import LoginModal from './LoginModal.vue'

export default {
  name: 'Header',
  components: {
    LoginModal
  },
  data() {
    return {
      showLogin: false,
      showRegister: false,
      showLanguageMenu: false,
      currentLanguage: '',
      languageList: [],
      navList: []
    }
  },
  computed: {
    isLoggedIn() {
      return !!getToken()
    },
    currentLanguageDisplay() {
      if (!this.currentLanguage) return 'En'
      const lang = this.languageList.find(l => l.value === this.currentLanguage)
      return lang ? lang.label : 'En'
    }
  },
  created() {
    this.getLanguageList()
    this.getNavList()
  },
  mounted() {
    // 监听点击外部关闭语言菜单
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    // 移除事件监听
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    getNavList() {
      this.$http.get('/index/navlist').then(res => {
        const result = res.data || []
        this.navList = result.map(item => {
          return {
            name: item.title,
            path: item.menu_url
          }
        })
      })
    },
    getLanguageList() {
      this.$http.get('/index/language_list').then(res => {
        const result = res.data || []
        this.languageList = result.map(item => {
          return {
            ...item,
            label: item.name,
            value: item.locale
          }
        })
        // 获取语言列表后，初始化当前语言
        this.initCurrentLanguage()
      })
    },
    initCurrentLanguage() {
      // 1. 首先检查本地存储是否有语言设置
      const savedLanguage = getLanguage()
      if (savedLanguage && savedLanguage !== 'en') {
        // 检查保存的语言是否在语言列表中
        const foundLang = this.languageList.find(lang => lang.value === savedLanguage)
        if (foundLang) {
          this.currentLanguage = savedLanguage
          return
        }
      }

      // 2. 如果本地没有，检查接口返回的语言列表中是否有 is_default = 1 的项
      if (this.languageList.length > 0) {
        const defaultLang = this.languageList.find(lang => lang.is_default === 1)
        if (defaultLang) {
          this.currentLanguage = defaultLang.value
          setLanguage(defaultLang.value)
          return
        }
      }

      // 3. 如果还没有，使用 'en' 作为默认值
      this.currentLanguage = 'en'
      if (!savedLanguage || savedLanguage === 'en') {
        setLanguage('en')
      }
    },
    handleLanguageSelect(langCode) {
      // 立即关闭菜单
      this.showLanguageMenu = false
      
      // 如果选择的是当前语言，不执行任何操作
      if (langCode === this.currentLanguage) {
        return
      }

      // 查找选中语言的 URL
      const selectedLang = this.languageList.find(lang => lang.value === langCode)
      if (selectedLang && selectedLang.url) {
        // 保留当前路径，只替换域名
        const currentPath = window.location.pathname
        const currentSearch = window.location.search
        const newUrl = selectedLang.url + currentPath + currentSearch
        window.location.href = newUrl
      }
    },
    handleCloseLanguageMenu() {
      this.showLanguageMenu = false
    },
    handleClickOutside(event) {
      // 如果点击的不是语言下拉菜单区域，关闭菜单
      if (this.$refs.languageDropdown && !this.$refs.languageDropdown.contains(event.target)) {
        this.showLanguageMenu = false
      }
    },
    handleLogin() {
      this.showLogin = true
      this.showRegister = false
    },
    handleRegister() {
      this.showRegister = true
      this.showLogin = false
    },
    handleCloseModal() {
      this.showLogin = false
      this.showRegister = false
    },
    handleLogoClick() {
      this.$router.push('/')
    },
    async handleLogout() {
      try {
        await this.$http.post('/auth/logout')
        removeToken()
        this.$router.go(0)
      } catch (error) {
        // 即使 API 调用失败，也清除本地 token
        removeToken()
        this.$router.go(0)
      }
    },
	// 点击跳转详情打开
	goFavorites() {
		if (this.isLoggedIn) {
			// 检查当前路由是否已经是 Favorites
			if (this.$route.name !== 'Favorites') {
			  this.$router.push({name: 'Favorites'})
			}
		} else {
			// console.info(this.$t('details_page.view_my_favorites_tips'));
			this.$toast(this.$t('details_page.view_my_favorites_tips'));
			this.showLogin = true;
		}
	}
	// 点击跳转详情打开	
  }
}
</script>

<style scoped lang="less">
.pc-header {
  background-color: #fff;
  position: fixed;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid #e5e5e5;
  width: 100%;
  font-size: 14px;
  padding: 25px 0;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);

  .header-content {
    max-width: 1730px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .logo {
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    .nav {
      display: flex;
      gap: 40px;
      align-items: center;

      .nav-item {
        text-decoration: none;
        font-size: 20px;
        font-weight: 600;
        transition: color 0.3s;
        color: #333;

        &:hover {
          color: #1e6865;
        }

        &.nav-item-active {
          color: #1e6865;
        }
      }
    }

    .language-dropdown {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 8px 16px;
      border-radius: 8px;
      transition: background-color 0.3s;
      margin-right: 20px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      .en-text {
        font-size: 18px;
        font-weight: 600;
        color: #333;
      }

      .arrow-icon {
        color: #666;
        transition: transform 0.3s;

        &.rotate {
          transform: rotate(180deg);
        }
      }

      // 下拉菜单
      .language-dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: 8px;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        min-width: 120px;
        z-index: 1000;
        overflow: hidden;
        animation: fadeInDown 0.2s ease-out;

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .language-option {
          padding: 12px 20px;
          font-size: 16px;
          color: #333;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
          white-space: nowrap;

          &:hover {
            background-color: #f5f5f5;
          }

          &.active {
            color: #1e6865;
            font-weight: 600;
            background-color: rgba(30, 104, 101, 0.1);
          }

          &:not(:last-child) {
            border-bottom: 1px solid #f0f0f0;
          }
        }
      }
    }

    .header-actions {
      position: relative;
      display: flex;
      align-items: center;
      .login-btn {
        display: flex;
        align-items: center;
        background-color: #ba3d38;
        border: none;
        color: #fff;
        height: 63px;
        border-radius: 40px;
        font-size: 20px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.3s;
        padding: 20px 30px;
        letter-spacing: 2px;
        box-shadow: 0px 4px 10px 0px rgba(49, 15, 13, 0.3);
        z-index: 99;

        &:hover {
          background-color: #a33530;
        }

        span {
          color: #fff;
          margin-left: 15px;
        }
      }

      .register-btn {
        padding: 20px 30px;
        background-color: #e9e9e9;
        box-shadow: none;
        color: #666;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        margin-left: -30px;
        padding-left: 50px;
        z-index: 1;
        &:hover {
          background-color: #d9d9d9;
        }
      }

      .logout-btn {
        padding: 20px 30px;
        &:hover {
          background-color: #a33530;
        }
      }
    }
  }
}
</style>

